/**
 * What this surface is running, in lines a person can read out loud.
 *
 * One bundle ships as both the web app and the payload the Tauri shell wraps,
 * so one card has to answer honestly for both — and the honest answers are
 * different. The desktop shell has a real version number (`tauri.conf.json`,
 * reported through `BootInfo.app_version`); the web has none, because a browser
 * is always running whatever was deployed last. Pretending the web has a
 * version would be worse than saying it does not: it would be a number that
 * never changes while the code underneath it does.
 *
 * So the web reports when the bundle was built, and the commit if the build was
 * given one. Both are injected by Vite at build time — see `vite.config.ts`.
 *
 * Pure so it can be tested. The runtime facts are read by the caller.
 */
import type { Platform } from './runtime.js';

/** Injected at build time. Empty string when the build had nothing to say. */
declare const __AEGIS_BUILT_AT__: string;
declare const __AEGIS_COMMIT__: string;

export interface BuildFacts {
  readonly platform: Platform;
  /** The shell's version on desktop. `'web'` in a browser — a sentinel. */
  readonly appVersion: string;
  /** ISO 8601, or empty. */
  readonly builtAt: string;
  /** Short or full sha, or empty. */
  readonly commit: string;
}

/** Reads what the bundle was told at build time. Safe before `define` runs. */
export function injectedBuild(): { builtAt: string; commit: string } {
  return {
    builtAt: typeof __AEGIS_BUILT_AT__ === 'string' ? __AEGIS_BUILT_AT__ : '',
    commit: typeof __AEGIS_COMMIT__ === 'string' ? __AEGIS_COMMIT__ : '',
  };
}

const SURFACE: Readonly<Record<Platform, string>> = {
  windows: 'Windows desktop',
  macos: 'macOS desktop',
  linux: 'Linux desktop',
  web: 'Web app',
};

export function surfaceLabel(platform: Platform): string {
  return SURFACE[platform] ?? 'Web app';
}

/** `abc1234567` -> `abc1234`. Left alone if already short. */
export function shortCommit(commit: string): string {
  const c = commit.trim();
  return c.length > 7 ? c.slice(0, 7) : c;
}

/**
 * The card's lines, in order, with nothing unknown rendered.
 *
 * An empty line is never emitted — a card reading "Built" with nothing after it
 * is worse than a card with one fewer line, and it is the shape a naive
 * template produces on a build that was given no commit.
 */
export function buildLines(facts: BuildFacts): readonly string[] {
  const lines: string[] = [];
  const surface = surfaceLabel(facts.platform);
  // `appVersion()` answers 'web' in a browser. That is a sentinel meaning "no
  // version exists here", not a version, and rendering it as one would read as
  // a product called AEGIS at version web.
  const version = facts.appVersion.trim();
  lines.push(
    facts.platform !== 'web' && version && version !== 'web'
      ? `AEGIS ${version} · ${surface}`
      : surface,
  );

  const builtAt = new Date(facts.builtAt);
  if (facts.builtAt && !Number.isNaN(builtAt.getTime())) {
    lines.push(`Built ${builtAt.toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })}`);
  }

  const commit = shortCommit(facts.commit);
  if (commit) lines.push(`Commit ${commit}`);

  return lines;
}
